import { Flex, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { AgendaStepProps } from '../types';
import { useGenerateMeetingAgenda } from '../hooks/useGenerateMeetingAgenda';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';

type Props = AgendaStepProps & {
  onSubmittingChange?: (isSubmitting: boolean) => void;
};

export function AgendaProcessingStep({ data, onSubmittingChange }: Props) {
  const { generateMeetingAgenda } = useGenerateMeetingAgenda();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const didSubmitRef = useRef(false);

  useEffect(() => {
    onSubmittingChange?.(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);

  const submitAgenda = async () => {
    if (!data.client?.id) {
      notifyAndLogError(
        'No client selected',
        'Please select a client and try again.',
        new Error('Missing client ID'),
      );
      return;
    }
    if (!data.selectedTemplateId) {
      notifyAndLogError(
        'No template selected',
        'Please select a template and try again.',
        new Error('Missing template ID'),
      );
      return;
    }

    setIsSubmitting(true);
    try {
      await generateMeetingAgenda(data.client.id, { templateId: data.selectedTemplateId });
    } catch (error) {
      notifyAndLogError('Failed to generate agenda', 'Please try again.', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!data.client?.id || didSubmitRef.current) return;
    didSubmitRef.current = true;
    void submitAgenda();
  }, [data]);

  return (
    <Flex direction="column" flex={1} h="100%" justify="center" align="center">
      <Text c="gray.10" fw={FONT_WEIGHTS.bold} size="displayMd">
        Fine tuning...
      </Text>
    </Flex>
  );
}
